import { useParams } from '@tanstack/react-router';
import { useCallback, useEffect, useState } from 'react';

import { previewRoom, type RoomPreview } from '../api.js';
import { GameTable } from '../components/GameTable.js';
import { JoinForm } from '../components/JoinForm.js';
import { Lobby } from '../components/Lobby.js';
import { readSeat, type StoredSeat } from '../session.js';
import { useRoom } from '../useRoom.js';
import styles from './RoomPage.module.css';

export function RoomPage() {
  const { roomId } = useParams({ from: '/room/$roomId' });
  const [seat, setSeat] = useState<StoredSeat | null>(() => readSeat(roomId));
  const [preview, setPreview] = useState<RoomPreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);

  useEffect(() => {
    if (seat) return;
    let cancelled = false;
    previewRoom(roomId)
      .then((p) => !cancelled && setPreview(p))
      .catch(() => !cancelled && setPreviewError('That room link is not valid any more.'));
    return () => {
      cancelled = true;
    };
  }, [roomId, seat]);

  const onJoined = useCallback((joined: StoredSeat) => setSeat(joined), []);

  if (!seat) {
    return (
      <main className={styles.centered}>
        {previewError ? (
          <p className={styles.notice} role="alert">
            {previewError}
          </p>
        ) : (
          <JoinForm roomId={roomId} preview={preview} onJoined={onJoined} />
        )}
      </main>
    );
  }

  return <ConnectedRoom roomId={roomId} resumeToken={seat.resumeToken} />;
}

function ConnectedRoom({ roomId, resumeToken }: { roomId: string; resumeToken: string }) {
  const room = useRoom(roomId, resumeToken);

  if (room.status === 'error') {
    return (
      <main className={styles.centered}>
        <p className={styles.notice} role="alert">
          {room.errorMessage ?? 'Something went wrong.'}
        </p>
      </main>
    );
  }

  if (!room.lobby) {
    return (
      <main className={styles.centered}>
        <p className={styles.notice}>Connecting to the table…</p>
      </main>
    );
  }

  return (
    <>
      {room.status === 'reconnecting' ? (
        <div className={styles.reconnecting} role="status">
          Reconnecting…
        </div>
      ) : null}
      {room.projection ? (
        <GameTable
          projection={room.projection}
          send={room.send}
          rejection={room.lastRejection}
          connectionStatus={room.status}
        />
      ) : (
        <Lobby lobby={room.lobby} viewerId={room.playerId} onHostCommand={room.hostCommand} />
      )}
    </>
  );
}
